import React, { useContext, useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ThemeContext } from "../assets/theme/ThemeContext";
import { getMetabaseReport } from "../API/index";

const MenuIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
  </svg>
);

const ITEM_ICON_PATHS = {
  timesheet: ["M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"],
  process: [
    "M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99",
  ],
  review: ["M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"],
  crews: [
    "M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z",
  ],
  user: [
    "M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z",
  ],
  folder: [
    "M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z",
  ],
  checklist: [
    "M9 12.75L11.25 15 15 9.75",
    "M4.5 5.25A2.25 2.25 0 016.75 3h10.5a2.25 2.25 0 012.25 2.25v13.5A2.25 2.25 0 0117.25 21H6.75a2.25 2.25 0 01-2.25-2.25V5.25z",
  ],
  report: [
    "M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z",
  ],
  power: ["M5.636 5.636a9 9 0 1012.728 0M12 3v9"],
  chevron: ["M19.5 8.25l-7.5 7.5-7.5-7.5"],
};

const ItemIcon = ({ name, className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className={className}
    aria-hidden="true"
  >
    {(ITEM_ICON_PATHS[name] || ITEM_ICON_PATHS.report).map((d) => (
      <path key={d} strokeLinecap="round" strokeLinejoin="round" d={d} />
    ))}
  </svg>
);

const SideDropDownOptions = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { updateTheme } = useContext(ThemeContext);
  const [isOpen, setIsOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [dynamicReports, setDynamicReports] = useState([]);
  const dropdownRef = useRef(null);

  const role = localStorage.getItem("role_name") || "User";
  const org_id = localStorage.getItem("organization_id") || "";
  const userName = localStorage.getItem("name") || "";
  // Additive capability: items marked approverVisible show for checklist
  // approvers regardless of which role their login resolved to.
  const isChecklistApprover = JSON.parse(localStorage.getItem("checklist_approver") || "false");

  const getInitials = (name = "") => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "";
    const first = parts[0][0];
    const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
    return (first + last).toUpperCase();
  };
  const userInitials = getInitials(userName);

  useEffect(() => {
    const fetchReports = async () => {
      try {
        const data = await getMetabaseReport();
        if (Array.isArray(data.data)) {
          // FIX-inactive-report-menu: filter at fetch time so every
          // downstream consumer (menu rendering, role/org checks) only
          // ever sees active reports.
          setDynamicReports(data.data.filter((r) => r.is_active));
        }
      } catch (error) {
        console.error("Error fetching metabase reports:", error);
      }
    };
    fetchReports();
  }, []);

  // Close on outside click or Escape
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const handleLogOut = () => {
    const defaultTheme = {
      primary_color: "#49adff",
      background_color_primary: "#fff",
      topbar_color: "#fff",
      logo: "",
    };
    updateTheme(defaultTheme);
    localStorage.clear();
    navigate("/");
    setIsOpen(false);
  };

  const normalizeRole = (value = "") => value.toLowerCase().replace(/\s+/g, "");
  const isGeneralForemanRole = (value = "") => {
    const normalizedRole = normalizeRole(value);
    return (
      normalizedRole.includes("generalforeman") ||
      normalizedRole.includes("generalforman")
    );
  };
  const matchesGeneralForemanReportRole = (allowedRole) => {
    if (allowedRole === role) return true;
    return isGeneralForemanRole(role) && isGeneralForemanRole(allowedRole);
  };

  /**
   * CENTRALIZED CONFIGURATION
   * Items are grouped into titled sections; empty sections are hidden.
   */
  const menuSections = [
    {
      title: "Timesheets",
      items: [
        {
          label: "Time Sheet",
          path: "/time-sheet",
          icon: "timesheet",
          allowedRoles: ["Admin", "Foreman", "Foreman GeneralForeman", "Foreman OpsManager"],
        },
        {
          label: "Process Timesheets",
          path: "/process-timesheet",
          icon: "process",
          allowedRoles: ["Admin"],
        },
        {
          label: "Review Timesheets",
          path: "/review-timesheet",
          icon: "review",
          approverVisible: true,
          allowedRoles: [
            "Admin",
            "Ops Manager",
            "General Foreman",
            "Foreman GeneralForeman",
            "Foreman OpsManager",
            "GeneralForeman Ops Manager",
          ],
        },
      ],
    },
    {
      title: null,
      items: [
        {
          label: "Project Checklist",
          path: "/project-checklist",
          icon: "checklist",
          requiredOrgId: "1",
          allowedRoles: [
            "Admin",
            "Ops Manager",
            "Foreman",
            "General Foreman",
            "Foreman GeneralForeman",
            "Foreman OpsManager",
            "GeneralForeman Ops Manager",
            "Checklist Approver",
          ],
        },
      ],
    },
    {
      title: "Manage",
      items: [
        {
          label: "Crews",
          path: "/all-crews-management",
          icon: "crews",
          allowedRoles: ["Admin", "General Foreman", "Foreman GeneralForeman"],
        },
        {
          label: "Users",
          path: "/users",
          icon: "user",
          allowedRoles: ["Admin"],
        },
        {
          label: "Projects",
          path: "/projects",
          icon: "folder",
          allowedRoles: ["Admin"],
        },
        {
          label: "Checklists",
          path: "/manage-checklist",
          icon: "checklist",
          allowedRoles: ["Admin"],
        },
      ],
    },
    {
      title: "Reports",
      items: [
        {
          label: "Customer Report",
          path: "/customer-report",
          icon: "report",
          allowedRoles: ["Admin"],
        },
        {
          label: "Unit Job Report",
          path: "/unit-job-report",
          icon: "report",
          requiredOrgId: "1",
          allowedRoles: [
            "Admin",
            "Ops Manager",
            "General Foreman",
            "Foreman GeneralForeman",
            "Foreman OpsManager",
            "GeneralForeman Ops Manager",
          ],
        },
        {
          label: "Report",
          icon: "report",
          allowedRoles: ["Admin", "Ops Manager", "General Foreman"],
          children: dynamicReports.map((report) => ({
            label: report.title,
            path: `/reports/${report.id}`,
            allowedRoles: Array.isArray(report.user_role)
              ? report.user_role
              : ["Admin", "Ops Manager", "General Foreman"],
            requiredOrgId: report.organization_id ? report.organization_id.toString() : undefined,
          })),
        },
      ],
    },
  ];

  const itemIsVisible = (item) => {
    const isReportItem = item.label === "Report" || item.path?.startsWith("/reports/");
    const matchesRole = isReportItem
      ? item.allowedRoles.some(matchesGeneralForemanReportRole)
      : item.allowedRoles.includes(role) ||
        Boolean(item.approverVisible && isChecklistApprover);
    const matchesOrg = item.requiredOrgId ? item.requiredOrgId === org_id : true;
    return matchesRole && matchesOrg;
  };

  // Items with children are kept only when at least one child survives filtering
  const filterItems = (items) =>
    items.filter(itemIsVisible).reduce((visible, item) => {
      if (item.children) {
        const children = item.children.filter(itemIsVisible);
        if (children.length > 0) visible.push({ ...item, children });
      } else {
        visible.push(item);
      }
      return visible;
    }, []);

  const visibleSections = menuSections
    .map((section) => ({ ...section, items: filterItems(section.items) }))
    .filter((section) => section.items.length > 0);

  const handleItemClick = (item) => {
    navigate(item.path);
    setIsOpen(false);
  };

  const toggleSubMenu = (label) => {
    setExpandedMenus((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  return (
    <div id="webtime-dropdown" ref={dropdownRef}>
      {/* Trigger Button: initials badge + menu icon act as a single pill */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label={userName ? `${userName} menu` : "Menu"}
        className={`wt-menu-trigger${isOpen ? " is-open" : ""}`}
      >
        {userInitials && (
          <span className="wt-menu-avatar" aria-hidden="true">
            {userInitials}
          </span>
        )}
        <MenuIcon />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="wt-menu-panel" role="menu" aria-orientation="vertical">
          {/* Identity strip: menu contents depend on the viewer's role */}
          <div className="wt-menu-identity">
            {userInitials && (
              <span className="wt-menu-avatar wt-menu-avatar--lg" aria-hidden="true">
                {userInitials}
              </span>
            )}
            <div className="wt-menu-identity-text">
              {userName && <p className="wt-menu-name">{userName}</p>}
              <span className="wt-menu-role">{role}</span>
            </div>
          </div>

          <div className="wt-menu-sections">
            {visibleSections.map((section, sectionIndex) => (
              <div key={section.title || `section-${sectionIndex}`} className="wt-menu-section">
                {section.title && (
                  <p className="wt-menu-section-title">{section.title}</p>
                )}
                {section.items.map((item) => {
                  if (item.children) {
                    const isExpanded = !!expandedMenus[item.label];
                    return (
                      <div key={item.label}>
                        <button
                          type="button"
                          role="menuitem"
                          aria-expanded={isExpanded}
                          onClick={() => toggleSubMenu(item.label)}
                          className="wt-menu-item"
                        >
                          <ItemIcon name={item.icon} />
                          <span>{item.label}</span>
                          <ItemIcon
                            name="chevron"
                            className={`wt-menu-chevron${isExpanded ? " is-open" : ""}`}
                          />
                        </button>
                        {isExpanded &&
                          item.children.map((child) => (
                            <button
                              key={child.path}
                              type="button"
                              role="menuitem"
                              onClick={() => handleItemClick(child)}
                              className={`wt-menu-item wt-menu-subitem${
                                location.pathname === child.path ? " is-active" : ""
                              }`}
                            >
                              <span>{child.label}</span>
                            </button>
                          ))}
                      </div>
                    );
                  }
                  return (
                    <button
                      key={item.path}
                      type="button"
                      role="menuitem"
                      onClick={() => handleItemClick(item)}
                      className={`wt-menu-item${location.pathname === item.path ? " is-active" : ""}`}
                    >
                      <ItemIcon name={item.icon} />
                      <span>{item.label}</span>
                    </button>
                  );
                })}
              </div>
            ))}

            {/* Logout - Always visible */}
            <div className="wt-menu-section">
              <button
                type="button"
                role="menuitem"
                onClick={handleLogOut}
                className="wt-menu-item wt-menu-item--logout"
              >
                <ItemIcon name="power" />
                <span>Log Out</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SideDropDownOptions;
